'use client';

/**
 * useTour — autoplay engine + proactive nudges (MOTION-SPEC §10).
 *
 * One requestAnimationFrame loop while playing, nothing else on a timer except
 * the idle nudge (a single setTimeout, re-armed on every interaction). Each
 * frame: elapsed → progress (0..1 inside the scene), beats crossed by `at` are
 * applied through `update()` and the active beat's hotspot becomes the
 * spotlight; at the end of the dwell `navigate(next)` is called once and the
 * loop waits for the scene to land (the scene change resets the clock). The
 * finale ends the tour without looping; `play()` afterwards restarts from the
 * first scene.
 *
 * `--sn-tour-p` is written on the `.experience` root every frame (bar fill,
 * dock ring). React only sees `progress` at 1/20 steps and on beat changes, so
 * the shell does not re-render sixty times a second.
 *
 * Interaction: `notifyInteraction()` pauses a playing tour, hides a nudge and
 * re-arms the idle timer. `document.visibilitychange` pauses too. Idle nudge:
 * `nudgeAfterMs` (7000) without interaction while not touring → spotlight
 * `LENS_BOOK[scene][persona].tryThis.hotspot`, once per `scene:persona` per
 * session (sessionStorage `sn-nudged`). A persona change while the nudge is
 * showing re-targets it. Reduced motion keeps the tour and the nudge (the
 * ring is static in CSS, spec DoD 7); `nudgeUnderReducedMotion: false` opts
 * out.
 *
 * Spotlight priority: playing → active beat hotspot; otherwise the lens card's
 * "try this" hover (`sn:spotlight` window event) → the nudge → null.
 */

import { useEffect, useMemo, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { REFERENCE_SCENES, type ReferenceDemoPatch, type ReferenceSceneId } from '../../reference/types';
import { LENS_BOOK } from '../personas';
import type { Bilingual, Persona, TourScript, TourState } from '../types';
import {
  NAV_WAIT_MS,
  NUDGE_AFTER_MS,
  NUDGE_STORAGE_KEY,
  TOUR_PROGRESS_VAR,
  beatIndexAt,
  nextScene,
  nudgeKey,
  parseNudged,
  progressStep,
  sceneEntry,
  serializeNudged,
} from './engine';
import { TOUR_SCRIPT } from './script';

export { TOUR_PROGRESS_VAR } from './engine';

/** Mirrors `LENS_SPOTLIGHT_EVENT` in lens/LensCard: {hotspot: string | null, source: 'lens'}. */
const LENS_SPOTLIGHT_EVENT = 'sn:spotlight';

export interface UseTourOptions {
  sceneId: ReferenceSceneId;
  persona: Persona;
  navigate: (target: ReferenceSceneId | number) => void;
  update: (patch: ReferenceDemoPatch) => void;
  reducedMotion: boolean;
  /** Idle time before the proactive nudge. Default 7000. */
  nudgeAfterMs?: number;
  /** False while a dialog is open, in deconstruct, on touch or the compact layout: no idle nudge then. Default true. */
  nudgeEnabled?: boolean;
  /** True while the scene crossfade runs (`data-scene-enter`); the idle timer arms once it clears. Default false. */
  sceneEntering?: boolean;
  /** Element receiving `--sn-tour-p` every frame. Default: the `[data-scene][data-tour]` root, resolved when play() starts. */
  progressRoot?: RefObject<HTMLElement | null>;
  /** Show the nudge under reduced motion (static ring). Default true, per the spec. */
  nudgeUnderReducedMotion?: boolean;
  /** Script override (previews, tests). Default TOUR_SCRIPT. */
  script?: TourScript;
}

export interface UseTourResult extends TourState {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Pause and rewind the current scene to 0 (used by a demo reset). */
  stop: () => void;
  /** Hotspot currently spotlighted by the tour, the lens hover or a nudge. */
  spotlight: string | null;
  /** Caption of the active beat (bilingual, resolved by the caller), null between beats. */
  caption: Bilingual | null;
  /** True once the finale dwell has ended; play() then restarts from the first scene. */
  finished: boolean;
  /** The proactive nudge target, null when none is showing. */
  nudge: string | null;
  dismissNudge: () => void;
  /** Call on any user interaction inside the scene. */
  notifyInteraction: () => void;
}

interface Snapshot {
  scene: ReferenceSceneId;
  playing: boolean;
  progress: number;
  beat: number;
  finished: boolean;
}

interface Latest {
  sceneId: ReferenceSceneId;
  persona: Persona;
  navigate: UseTourOptions['navigate'];
  update: UseTourOptions['update'];
  script: TourScript;
  nudgeAfterMs: number;
  /** nudgeEnabled combined with the reduced-motion gate. */
  nudgeAllowed: boolean;
  sceneEntering: boolean;
  progressRoot: RefObject<HTMLElement | null> | undefined;
}

interface Engine {
  sceneId: ReferenceSceneId;
  playing: boolean;
  finished: boolean;
  /** ms into the scene; frozen while paused. */
  elapsed: number;
  /** Virtual scene start on the rAF timeline while playing. */
  startedAt: number;
  beat: number;
  /** Last progress step published to React. */
  step: number;
  /** rAF timestamp of the navigate() call at the end of a scene; 0 when none is pending. */
  navRequestedAt: number;
  frame: number;
  root: HTMLElement | null;
  nudge: string | null;
  idleTimer: number;
  nudged: Set<string> | null;
}

interface Control {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  stop: () => void;
  notifyInteraction: () => void;
  dismissNudge: () => void;
  /** Re-evaluate the idle timer after a gate changed (nudgeEnabled, sceneEntering, delay). */
  rearm: () => void;
  onSceneChange: (scene: ReferenceSceneId) => void;
  onPersonaChange: (persona: Persona) => void;
  onVisibility: () => void;
  dispose: () => void;
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage is a convenience; the nudge then repeats after a reload only.
  }
}

function createControl(
  latest: RefObject<Latest>,
  setSnapshot: Dispatch<SetStateAction<Snapshot>>,
  setNudgeState: Dispatch<SetStateAction<string | null>>,
): Control {
  const e: Engine = {
    sceneId: latest.current.sceneId,
    playing: false,
    finished: false,
    elapsed: 0,
    startedAt: 0,
    beat: -1,
    step: -1,
    navRequestedAt: 0,
    frame: 0,
    root: null,
    nudge: null,
    idleTimer: 0,
    nudged: null,
  };

  const publish = (patch: Partial<Snapshot>) => {
    setSnapshot((prev) => {
      for (const key of Object.keys(patch) as Array<keyof Snapshot>) {
        if (prev[key] !== patch[key]) return { ...prev, ...patch };
      }
      return prev;
    });
  };

  const setNudge = (hotspot: string | null) => {
    if (e.nudge === hotspot) return;
    e.nudge = hotspot;
    setNudgeState(hotspot);
  };

  const resolveRoot = (): HTMLElement | null =>
    latest.current.progressRoot?.current ?? document.querySelector<HTMLElement>('[data-scene][data-tour]');

  const writeVar = (progress: number) => {
    e.root?.style.setProperty(TOUR_PROGRESS_VAR, progress.toFixed(4));
  };

  const stopFrame = () => {
    if (e.frame) {
      cancelAnimationFrame(e.frame);
      e.frame = 0;
    }
  };

  const clearIdle = () => {
    if (e.idleTimer) {
      window.clearTimeout(e.idleTimer);
      e.idleTimer = 0;
    }
  };

  const nudgedKeys = (): Set<string> => {
    if (!e.nudged) e.nudged = parseNudged(readSession(NUDGE_STORAGE_KEY));
    return e.nudged;
  };

  const rememberNudged = (key: string) => {
    const keys = nudgedKeys();
    if (keys.has(key)) return;
    keys.add(key);
    writeSession(NUDGE_STORAGE_KEY, serializeNudged(keys));
  };

  const canNudge = (): boolean => {
    const l = latest.current;
    return l.nudgeAllowed && !l.sceneEntering && !e.playing && document.visibilityState !== 'hidden';
  };

  const armIdle = () => {
    clearIdle();
    if (!canNudge()) return;
    const l = latest.current;
    if (nudgedKeys().has(nudgeKey(l.sceneId, l.persona))) return;
    e.idleTimer = window.setTimeout(() => {
      e.idleTimer = 0;
      if (!canNudge()) return;
      const now = latest.current;
      const key = nudgeKey(now.sceneId, now.persona);
      if (nudgedKeys().has(key)) return;
      rememberNudged(key);
      setNudge(LENS_BOOK[now.sceneId][now.persona].tryThis.hotspot);
    }, l.nudgeAfterMs);
  };

  function finish() {
    const { duration } = sceneEntry(latest.current.script, e.sceneId);
    e.playing = false;
    e.finished = true;
    e.elapsed = duration;
    e.navRequestedAt = 0;
    stopFrame();
    writeVar(1);
    publish({ playing: false, progress: 1, beat: e.beat, finished: true });
    armIdle();
  }

  function pause() {
    if (!e.playing) return;
    const { duration } = sceneEntry(latest.current.script, e.sceneId);
    e.elapsed = Math.min(duration, Math.max(0, performance.now() - e.startedAt));
    e.playing = false;
    e.navRequestedAt = 0;
    stopFrame();
    const progress = e.elapsed / duration;
    writeVar(progress);
    publish({ playing: false, progress, beat: e.beat });
    armIdle();
  }

  function tick(now: number) {
    e.frame = 0;
    if (!e.playing) return;
    const l = latest.current;
    const { duration, beats } = sceneEntry(l.script, e.sceneId);
    const raw = now - e.startedAt;
    const elapsed = raw < 0 ? 0 : raw;
    e.elapsed = elapsed > duration ? duration : elapsed;
    const progress = e.elapsed / duration;
    writeVar(progress);

    const beat = beatIndexAt(beats, elapsed, e.beat);
    const beatChanged = beat !== e.beat;
    if (beatChanged) {
      for (let index = e.beat + 1; index <= beat; index += 1) {
        const patch = beats[index].patch;
        if (patch) l.update(patch);
      }
      e.beat = beat;
    }
    const step = progressStep(progress);
    if (beatChanged || step !== e.step) {
      e.step = step;
      publish({ progress, beat });
    }

    if (elapsed >= duration) {
      const next = nextScene(e.sceneId);
      if (next === null) {
        finish();
        return;
      }
      if (e.navRequestedAt === 0) {
        e.navRequestedAt = now;
        l.navigate(next);
      } else if (now - e.navRequestedAt > NAV_WAIT_MS) {
        // navigate() never landed (aborted by a competing navigation): stop quietly.
        pause();
        return;
      }
    }
    e.frame = requestAnimationFrame(tick);
  }

  function play() {
    if (e.playing) return;
    const l = latest.current;
    if (e.finished) {
      e.finished = false;
      e.elapsed = 0;
      e.beat = -1;
      e.step = -1;
      if (e.sceneId !== REFERENCE_SCENES[0]) l.navigate(REFERENCE_SCENES[0]);
    }
    e.playing = true;
    e.navRequestedAt = 0;
    e.startedAt = performance.now() - e.elapsed;
    e.root = resolveRoot();
    clearIdle();
    setNudge(null);
    const { duration } = sceneEntry(l.script, e.sceneId);
    const progress = e.elapsed / duration;
    writeVar(progress);
    publish({ scene: e.sceneId, playing: true, progress, beat: e.beat, finished: false });
    if (!e.frame) e.frame = requestAnimationFrame(tick);
  }

  function toggle() {
    if (e.playing) pause();
    else play();
  }

  function rewind() {
    e.elapsed = 0;
    e.beat = -1;
    e.step = -1;
    e.navRequestedAt = 0;
    e.finished = false;
    e.startedAt = performance.now();
  }

  function stop() {
    e.playing = false;
    stopFrame();
    rewind();
    writeVar(0);
    publish({ playing: false, progress: 0, beat: -1, finished: false });
    armIdle();
  }

  function notifyInteraction() {
    if (e.playing) pause();
    setNudge(null);
    armIdle();
  }

  function dismissNudge() {
    setNudge(null);
  }

  function rearm() {
    if (!latest.current.nudgeAllowed) setNudge(null);
    armIdle();
  }

  function onSceneChange(scene: ReferenceSceneId) {
    if (e.sceneId === scene) return;
    e.sceneId = scene;
    rewind();
    if (e.playing && !e.root) e.root = resolveRoot();
    writeVar(0);
    publish({ scene, progress: 0, beat: -1, finished: false });
    setNudge(null);
    armIdle();
  }

  function onPersonaChange(persona: Persona) {
    const scene = latest.current.sceneId;
    if (e.nudge !== null && canNudge()) {
      // Re-target the visible nudge to the new lens's suggestion.
      rememberNudged(nudgeKey(scene, persona));
      setNudge(LENS_BOOK[scene][persona].tryThis.hotspot);
      return;
    }
    armIdle();
  }

  function onVisibility() {
    if (document.visibilityState === 'hidden') {
      if (e.playing) pause();
      clearIdle();
      return;
    }
    armIdle();
  }

  function dispose() {
    e.playing = false;
    stopFrame();
    clearIdle();
  }

  return { play, pause, toggle, stop, notifyInteraction, dismissNudge, rearm, onSceneChange, onPersonaChange, onVisibility, dispose };
}

export function useTour(options: UseTourOptions): UseTourResult {
  const {
    sceneId,
    persona,
    navigate,
    update,
    reducedMotion,
    nudgeAfterMs = NUDGE_AFTER_MS,
    nudgeEnabled = true,
    sceneEntering = false,
    progressRoot,
    nudgeUnderReducedMotion = true,
    script = TOUR_SCRIPT,
  } = options;
  const nudgeAllowed = nudgeEnabled && (nudgeUnderReducedMotion || !reducedMotion);

  const latest = useRef<Latest>({
    sceneId,
    persona,
    navigate,
    update,
    script,
    nudgeAfterMs,
    nudgeAllowed,
    sceneEntering,
    progressRoot,
  });
  latest.current = { sceneId, persona, navigate, update, script, nudgeAfterMs, nudgeAllowed, sceneEntering, progressRoot };

  const [snapshot, setSnapshot] = useState<Snapshot>({ scene: sceneId, playing: false, progress: 0, beat: -1, finished: false });
  const [nudge, setNudge] = useState<string | null>(null);
  const [lensSpot, setLensSpot] = useState<string | null>(null);

  const controlRef = useRef<Control | null>(null);
  if (controlRef.current === null) controlRef.current = createControl(latest, setSnapshot, setNudge);
  const control = controlRef.current;

  /* Scene landed: the clock restarts (also for the tour's own navigate). */
  useEffect(() => {
    control.onSceneChange(sceneId);
  }, [control, sceneId]);

  /* Persona: re-target a visible nudge or re-arm for the new key. */
  const personaSeenRef = useRef(persona);
  useEffect(() => {
    if (personaSeenRef.current === persona) return;
    personaSeenRef.current = persona;
    control.onPersonaChange(persona);
  }, [control, persona]);

  /* Idle gate changes (dialog, deconstruct, touch, crossfade, delay) and the initial arm. */
  useEffect(() => {
    control.rearm();
  }, [control, nudgeAllowed, sceneEntering, nudgeAfterMs]);

  /* Background tab pauses the tour and suspends the idle timer. */
  useEffect(() => {
    const onVisibility = () => control.onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [control]);

  /* Lens card "try this" hover / focus. */
  useEffect(() => {
    const onSpotlight = (event: Event) => {
      const detail = (event as CustomEvent<{ hotspot?: string | null } | undefined>).detail;
      setLensSpot(typeof detail?.hotspot === 'string' && detail.hotspot ? detail.hotspot : null);
    };
    window.addEventListener(LENS_SPOTLIGHT_EVENT, onSpotlight);
    return () => window.removeEventListener(LENS_SPOTLIGHT_EVENT, onSpotlight);
  }, []);

  useEffect(() => () => control.dispose(), [control]);

  /* Derived: active beat (only when the snapshot describes the scene on screen). */
  const current = snapshot.scene === sceneId ? snapshot : null;
  const beat = current ? current.beat : -1;
  const activeBeat = useMemo(() => {
    if (beat < 0) return null;
    const { beats } = sceneEntry(script, sceneId);
    return beats[beat] ?? null;
  }, [beat, sceneId, script]);

  const playing = snapshot.playing;
  const progress = current ? current.progress : 0;
  const finished = current ? current.finished : false;
  const caption = playing || finished || progress > 0 ? activeBeat?.caption ?? null : null;
  const beatSpot = playing ? activeBeat?.hotspot ?? null : null;
  const spotlight = playing ? beatSpot : lensSpot ?? nudge;

  return useMemo<UseTourResult>(() => ({
    playing,
    progress,
    beat,
    finished,
    nudge,
    spotlight,
    caption,
    play: control.play,
    pause: control.pause,
    toggle: control.toggle,
    stop: control.stop,
    dismissNudge: control.dismissNudge,
    notifyInteraction: control.notifyInteraction,
  }), [beat, caption, control, finished, nudge, playing, progress, spotlight]);
}
